import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, useLocation } from "react-router-dom";
import { accountKeys, getAccountNavigation, type InternalPage } from "@rekonas/data-access";

const labels: Record<InternalPage, string> = {
  operations: "Operations",
  "metrics-contexts": "Metrics contexts", "ai-chat": "Recording assistant", "ai-prompts": "AI prompts",
  "workspace-access": "Workspace access", "analysis-runs": "Analysis runs", "report-import": "Report import",
};
export function InternalMenu() {
  const query = useQuery({ queryKey: accountKeys.navigation(), queryFn: ({ signal }) => getAccountNavigation(signal),
    retry: false, gcTime: 0, staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: true, refetchInterval: 15_000 });
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null), trigger = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const pages = query.isError ? [] : query.data || [];
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);
  useEffect(() => {
    if (open) root.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);
  if (!pages.length) return null;
  const close = () => { setOpen(false); trigger.current?.focus(); };
  return <div className="internal-menu" ref={root} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false); }}
    onKeyDown={e => {
      if (e.key === "Escape") { e.preventDefault(); close(); }
      if (["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) {
        e.preventDefault();
        if (!open) { setOpen(true); return; }
        const items = Array.from(root.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || []);
        const current = items.indexOf(document.activeElement as HTMLElement);
        const next = e.key === "Home" ? 0 : e.key === "End" ? items.length - 1 : (current + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
        items[next]?.focus();
      }
    }}>
    <button ref={trigger} className={`operational-nav-link${location.pathname.startsWith("/internal/") ? " operational-nav-active" : ""}`}
      type="button" aria-haspopup="menu" aria-expanded={open} aria-controls="internal-navigation" onClick={() => setOpen(v => !v)}>Internal <span aria-hidden="true">▾</span></button>
    {open && <div id="internal-navigation" role="menu" aria-label="Internal pages" className="internal-menu-items">
      {pages.map(page => <NavLink key={page} role="menuitem" tabIndex={-1} to={`/internal/${page}`}
        className={({ isActive }) => isActive ? "internal-link active" : "internal-link"} onClick={close}>{labels[page]}</NavLink>)}
    </div>}
  </div>;
}
